import React from 'react';
import { motion } from 'framer-motion';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import BlogList from './BlogList';
import BlogPost from './BlogPost';
import useIsMobile from './useIsMobile';
import PrivacyPolicy from './PrivacyPolicy';
import Team from './Team';
import Navbar from './Navbar';
import Footer from './Footer';
import ScrollToTop from './ScrollToTop';
import WalkingPanda from './WalkingPanda';
import PandaHero from './PandaHero';
import NeuralBg from './NeuralBg';


function HomePage() {
  const isMobile = useIsMobile();

  return (
    <div className="App">
      <Navbar />

      {/* Hero Section */}
      <section className="hero">
        <div className="container hero-container">
          <motion.div
            className="hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <PandaHero />
            <p className="hero-company-tag">by Steepwind Labs</p>
            <h1 className="hero-heading">
              Your phone. Your voice.<br />Panda does the rest.
            </h1>
            <p className="hero-description">
              Panda listens to your voice and autonomously navigates any Android app — from messaging to shopping to streaming. No tapping. No menus. Just speak.
            </p>
            <div className="hero-actions">
              <a
                href="https://play.google.com/store/apps/details?id=com.blurr.voice&pcampaignid=web_share"
                target="_blank"
                rel="noopener noreferrer"
                className="play-store-btn"
              >
                <img
                  src="/gplay.svg"
                  alt="Get it on Google Play"
                  className="play-store-img"
                />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats section">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="hero-subheading" style={{ marginBottom: '32px' }}>
              Trusted by <span style={{ color: 'var(--text-primary)' }}>Thousands</span>
            </h2>
            <p className="section-subtitle">
              Join the growing community of users who have transformed their Android experience
            </p>
          </motion.div>

          <div className="stats-grid">
            <motion.div
              className="stat-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <div className="stat-number">150K+</div>
              <div className="stat-label">Active Users</div>
              <div className="stat-description">Growing Android community</div>
            </motion.div>

            <motion.div
              className="stat-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="stat-number">890</div>
              <div className="stat-label">GitHub Stars</div>
              <div className="stat-description">Open source community support</div>
              <a
                href="https://github.com/ayush0chaudhary/blurr"
                target="_blank"
                rel="noopener noreferrer"
                className="github-link"
              >
                <img src="/github.png" alt="GitHub" className="github-icon" />
                <span className="repo-name">ayush0chaudhary/blurr</span>
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Product Features Section */}
      <section className="features section" id="features">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title" style={{ color: 'var(--text-primary)', fontWeight: 'normal' }}>
              What Panda <span style={{ color: 'var(--text-primary)' }}>Does</span>
            </h2>
            <p className="section-subtitle">
              A digital-native AI product that turns your voice into action. Panda connects with over a thousand apps to autonomously navigate and operate any interface for you.
            </p>
          </motion.div>

          <div className="features-grid">
            <motion.div
              className="feature-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <div className="feature-icon">🎙️</div>
              <h3 className="feature-title">Voice Control</h3>
              <p className="feature-text">
                Speak naturally and Panda operates your phone. No menus, no tapping — just say what you need and it's done.
              </p>
            </motion.div>

            <motion.div
              className="feature-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="feature-icon">👁️</div>
              <h3 className="feature-title">Screen Understanding</h3>
              <p className="feature-text">
                AI reads and understands what's on your screen in real-time, making intelligent decisions about what to tap and where to navigate.
              </p>
            </motion.div>

            <motion.div
              className="feature-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <div className="feature-icon">⚡</div>
              <h3 className="feature-title">App Automation</h3>
              <p className="feature-text">
                Automate multi-step tasks across any Android app — from ordering food to messaging friends to shopping online.
              </p>
            </motion.div>

            <motion.div
              className="feature-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <div className="feature-icon">🧠</div>
              <h3 className="feature-title">Smart Memory</h3>
              <p className="feature-text">
                Panda remembers your preferences, contacts, and context — getting smarter and more personalized with every interaction.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Scrollable Videos Section */}
      <section className="video-showcase section">
        <video
          className="background-video"
          autoPlay
          muted
          loop
          playsInline
        >
          <source src="/sea.mp4" type="video/mp4" />
        </video>
        <div className="container">
          <motion.div
            className="section-header"
            style={{ position: 'relative', zIndex: 2 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title" style={{ color: 'var(--text-primary)', fontWeight: 'normal' }}>
              See <span style={{ color: 'var(--text-primary)' }}>Panda</span> in Action
            </h2>
            <p className="section-subtitle" style={{ fontWeight: 'normal' }}>
              Watch how Panda transforms your Android experience
            </p>
          </motion.div>

          <div className="video-showcase-container">
            <div className="video-side">
              <div className="video-item" data-video="bb">
                <video
                  className="showcase-video"
                  muted
                  loop
                  playsInline
                  // --- Desktop hover logic ---
                  onMouseEnter={!isMobile ? (e) => e.target.play() : null}
                  onMouseLeave={!isMobile ? (e) => e.target.pause() : null}
                  // --- Mobile tap-to-play/pause logic ---
                  onClick={isMobile ? (e) => {
                    if (e.target.paused) {
                      e.target.play();
                    } else {
                      e.target.pause();
                    }
                  } : null}
                >
                  <source src="/bb.mp4" type="video/mp4" />
                </video>

                {/* --- This button will now only show on desktop --- */}
                {!isMobile && (
                  <button
                    className="audio-toggle-btn"
                    // --- THIS IS THE FULL LOGIC THAT WAS MISSING ---
                    onClick={(e) => {
                      const allVideos = document.querySelectorAll('.showcase-video');
                      const allButtons = document.querySelectorAll('.audio-toggle-btn');
                      const isCurrentlyMuted = allVideos[0].muted;

                      allVideos.forEach(video => {
                        video.muted = !isCurrentlyMuted;
                      });

                      allButtons.forEach(button => {
                        const icon = button.querySelector('.audio-icon');
                        icon.textContent = isCurrentlyMuted ? '🔊' : '🔇';
                      });
                    }}
                  >
                    <span className="audio-icon">🔇</span>
                  </button>
                )}
              </div>

              <div className="video-item" data-video="tie">
                <video
                  className="showcase-video"
                  muted
                  loop
                  playsInline
                  // --- Desktop hover logic ---
                  onMouseEnter={!isMobile ? (e) => e.target.play() : null}
                  onMouseLeave={!isMobile ? (e) => e.target.pause() : null}
                  // --- Mobile tap-to-play/pause logic ---
                  onClick={isMobile ? (e) => {
                    if (e.target.paused) {
                      e.target.play();
                    } else {
                      e.target.pause();
                    }
                  } : null}
                >
                  <source src="/tie.mp4" type="video/mp4" />
                </video>

                {/* --- This button will now only show on desktop --- */}
                {!isMobile && (
                  <button
                    className="audio-toggle-btn"
                    // --- THIS IS THE FULL LOGIC THAT WAS MISSING ---
                    onClick={(e) => {
                      const allVideos = document.querySelectorAll('.showcase-video');
                      const allButtons = document.querySelectorAll('.audio-toggle-btn');
                      const isCurrentlyMuted = allVideos[0].muted;

                      allVideos.forEach(video => {
                        video.muted = !isCurrentlyMuted;
                      });

                      allButtons.forEach(button => {
                        const icon = button.querySelector('.audio-icon');
                        icon.textContent = isCurrentlyMuted ? '🔊' : '🔇';
                      });
                    }}
                  >
                    <span className="audio-icon">🔇</span>
                  </button>
                )}
              </div>

              <div className="video-item" data-video="amz">
                <video
                  className="showcase-video"
                  muted
                  loop
                  playsInline
                  // --- Desktop hover logic ---
                  onMouseEnter={!isMobile ? (e) => e.target.play() : null}
                  onMouseLeave={!isMobile ? (e) => e.target.pause() : null}
                  // --- Mobile tap-to-play/pause logic ---
                  onClick={isMobile ? (e) => {
                    if (e.target.paused) {
                      e.target.play();
                    } else {
                      e.target.pause();
                    }
                  } : null}
                >
                  <source src="/amz.mp4" type="video/mp4" />
                </video>

                {/* --- This button will now only show on desktop --- */}
                {!isMobile && (
                  <button
                    className="audio-toggle-btn"
                    // --- THIS IS THE FULL LOGIC THAT WAS MISSING ---
                    onClick={(e) => {
                      const allVideos = document.querySelectorAll('.showcase-video');
                      const allButtons = document.querySelectorAll('.audio-toggle-btn');
                      const isCurrentlyMuted = allVideos[0].muted;

                      allVideos.forEach(video => {
                        video.muted = !isCurrentlyMuted;
                      });

                      allButtons.forEach(button => {
                        const icon = button.querySelector('.audio-icon');
                        icon.textContent = isCurrentlyMuted ? '🔊' : '🔇';
                      });
                    }}
                  >
                    <span className="audio-icon">🔇</span>
                  </button>
                )}
              </div>
            </div>

            <div className="content-side">
              <div className="content-item" data-content="bb">
                <h3 style={{ color: 'var(--text-primary)', fontWeight: 'normal' }}>Voice Commands</h3>
                <p style={{ fontWeight: 'normal' }}>Watch Panda open Netflix and play Breaking Bad with just a voice command. Control your phone with natural language - Panda understands your voice and executes complex tasks across any app.</p>
              </div>

              <div className="content-item" data-content="tie">
                <h3 style={{ color: 'var(--text-primary)', fontWeight: 'normal' }}>Smart Automation</h3>
                <p style={{ fontWeight: 'normal' }}>See Panda find a video on TikTok about how to tie a tie. Panda can navigate interfaces, search content, and perform multi-step operations with intelligent screen understanding across any app.</p>
              </div>

              <div className="content-item" data-content="amz">
                <h3 style={{ color: 'var(--text-primary)', fontWeight: 'normal' }}>Smart Search</h3>
                <p style={{ fontWeight: 'normal' }}>Watch Panda open Amazon and search for "USB 3 cable 3 feet long". Panda can navigate e-commerce sites, perform precise searches, and help you find exactly what you need with intelligent automation.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Accessibility & Impact Section */}
      <section className="impact section">
        <div className="container">
          <div className="impact-content">
            <motion.div
              className="impact-text-side"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="section-title" style={{ color: 'var(--text-primary)', fontWeight: 'normal', textAlign: 'left' }}>
                Building for <span style={{ color: 'var(--text-primary)' }}>Accessibility</span>
              </h2>
              <p className="impact-description">
                Panda isn't just another app — it's a tool for digital independence. For visually impaired users,
                traditional screen readers still place the burden of navigation on the user. Panda flips this by
                being an <strong>active agent</strong> that performs actions on behalf of the user.
              </p>
              <p className="impact-description">
                Our approach was validated by the <strong>National Association for the Blind (NAB), Delhi</strong>,
                who recognized Panda's potential to transform how visually impaired people interact with their phones.
              </p>
              <p className="impact-quote">
                "Instead of learning complex gestures, users simply state their goal — and Panda handles everything."
              </p>
            </motion.div>
            <motion.div
              className="impact-image-side"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <img src="/nab-delhi.png" alt="Panda AI validated by NAB Delhi" className="impact-image" />
              <p className="impact-caption">Validated by NAB Delhi — National Association for the Blind</p>
            </motion.div>
          </div>
        </div>
      </section>



      {/* Pricing Section */}
      <section className="pricing section" id="pricing">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title" style={{ color: 'var(--text-primary)', fontWeight: 'normal' }}>
              Simple <span style={{ color: 'var(--text-primary)' }}>Pricing</span>
            </h2>
            <p className="section-subtitle">
              Start free. Upgrade when you need more.
            </p>
          </motion.div>

          <div className="pricing-grid">
            {/* Free Tier */}
            <motion.div
              className="pricing-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <div className="pricing-badge">Get Started</div>
              <h3 className="pricing-plan">Free</h3>
              <div className="pricing-price">
                <span className="price-amount">$0</span>
                <span className="price-period">forever</span>
              </div>
              <p className="pricing-description">Perfect for trying out Panda</p>
              <ul className="pricing-features">
                <li>✦ 30 lifetime credits</li>
                <li>✦ Voice commands & automation</li>
                <li>✦ Screen understanding</li>
                <li>✦ On-device wake word</li>
              </ul>
              <a
                href="https://play.google.com/store/apps/details?id=com.blurr.voice"
                target="_blank"
                rel="noopener noreferrer"
                className="pricing-cta"
              >
                Download Free
              </a>
            </motion.div>

            {/* Pro Tier */}
            <motion.div
              className="pricing-card pricing-card-featured"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="pricing-badge pricing-badge-pro">Most Popular</div>
              <h3 className="pricing-plan">Pro</h3>
              <div className="pricing-price">
                <span className="price-amount">$8</span>
                <span className="price-period">/month</span>
              </div>
              <p className="pricing-yearly">or $80/year (save 17%)</p>
              <p className="pricing-description">For power users who rely on Panda daily</p>
              <ul className="pricing-features">
                <li>✦ Unlimited credits</li>
                <li>✦ Everything in Free</li>
                <li>✦ Priority AI processing</li>
                <li>✦ Memory & personalization</li>
                <li>✦ Priority support</li>
              </ul>
              <a
                href="https://play.google.com/store/apps/details?id=com.blurr.voice"
                target="_blank"
                rel="noopener noreferrer"
                className="pricing-cta pricing-cta-pro"
              >
                Get Pro
              </a>
            </motion.div>

            {/* BYOK Tier */}
            <motion.div
              className="pricing-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <div className="pricing-badge">Developer</div>
              <h3 className="pricing-plan">BYOK</h3>
              <div className="pricing-price">
                <span className="price-amount">$1</span>
                <span className="price-period">/month</span>
              </div>
              <p className="pricing-yearly">or $10/year (save 17%)</p>
              <p className="pricing-description">Bring Your Own API Key</p>
              <ul className="pricing-features">
                <li>✦ Use your own API keys</li>
                <li>✦ Everything in Free</li>
                <li>✦ Full control over AI models</li>
                <li>✦ Unlimited usage</li>
              </ul>
              <a
                href="https://play.google.com/store/apps/details?id=com.blurr.voice"
                target="_blank"
                rel="noopener noreferrer"
                className="pricing-cta"
              >
                Get BYOK
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Us */}
      <Footer />

      <style jsx>{`
        /* Hero Enhancements */
        .hero-company-tag {
          display: inline-block;
          margin-top: 16px;
          font-size: 0.78rem;
          font-weight: 600;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: var(--primary-color);
          background: rgba(16, 185, 129, 0.1);
          border: 1px solid rgba(16, 185, 129, 0.25);
          border-radius: 20px;
          padding: 4px 14px;
        }

        .hero-heading {
          font-size: 3.2rem;
          font-weight: 700;
          color: var(--text-primary);
          margin-top: 20px;
          line-height: 1.15;
          text-align: center;
          max-width: 700px;
          margin-left: auto;
          margin-right: auto;
          letter-spacing: -0.02em;
        }

        .hero-description {
          font-size: 1.15rem;
          font-weight: 400;
          color: var(--text-secondary);
          margin-top: 16px;
          line-height: 1.6;
          text-align: center;
          max-width: 600px;
          margin-left: auto;
          margin-right: auto;
        }

        /* Features Section */
        .features {
          background: var(--darker-color);
          padding: 80px 0;
        }

        .features-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 24px;
          max-width: 1100px;
          margin: 0 auto;
        }

        .feature-card {
          background: #FFFFFF;
          border: 1px solid rgba(0, 0, 0, 0.08);
          border-radius: 20px;
          padding: 32px 24px;
          text-align: center;
          box-shadow: 0 2px 8px rgba(0,0,0,0.06);
          transition: all 0.3s ease;
        }

        .feature-card:hover {
          border-color: rgba(5, 150, 105, 0.25);
          transform: translateY(-5px);
          box-shadow: 0 12px 32px rgba(0, 0, 0, 0.1);
        }

        .feature-icon {
          font-size: 2.5rem;
          margin-bottom: 16px;
        }

        .feature-title {
          font-size: 1.15rem;
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 12px;
        }

        .feature-text {
          font-size: 0.9rem;
          line-height: 1.6;
          color: var(--text-secondary);
        }

        /* Accessibility & Impact Section */
        .impact {
          background: var(--darker-color);
          padding: 80px 0;
        }

        .impact-content {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 60px;
          align-items: center;
          max-width: 1100px;
          margin: 0 auto;
        }

        .impact-description {
          font-size: 1.05rem;
          line-height: 1.7;
          color: var(--text-secondary);
          margin-bottom: 16px;
        }

        .impact-description strong {
          color: var(--text-primary);
        }

        .impact-quote {
          font-size: 1.1rem;
          font-style: italic;
          color: var(--text-secondary);
          margin-top: 20px;
          padding-left: 20px;
          border-left: 3px solid var(--primary-color);
        }

        .impact-image {
          width: 100%;
          border-radius: 16px;
          border: 1px solid rgba(0, 0, 0, 0.08);
          box-shadow: 0 4px 16px rgba(0,0,0,0.1);
        }

        .impact-caption {
          font-size: 0.85rem;
          color: var(--text-muted);
          text-align: center;
          margin-top: 12px;
        }

        .footer-address {
          font-size: 0.8rem;
          opacity: 0.5;
          margin-top: 8px;
        }

        .hero {
          min-height: 100vh;
          display: flex;
          align-items: center;
          background: transparent;
          color: var(--text-primary);
          position: relative;
          overflow: hidden;
        }

        .hero::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: 
            radial-gradient(circle at 20% 80%, rgba(16, 185, 129, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(59, 130, 246, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 40% 40%, rgba(6, 182, 212, 0.05) 0%, transparent 50%);
          pointer-events: none;
          z-index: 1;
        }

.hero::after {
           content: '';
           position: absolute;
           bottom: 0;
           left: 0;
           right: 0;
           height: 400px;
           background: linear-gradient(to top, var(--darker-color) 0%, var(--darker-color) 100px, transparent 100%);
           z-index: 2;
           pointer-events: none;
         }

         .hero-container {
           display: flex;
           align-items: center;
           justify-content: center;
           height: 100vh;
           position: relative;
           z-index: 3;
         }

         .hero-content {
           text-align: center;
           position: relative;
           z-index: 3;
         }

         /* --- Hero panda rig --- */
         @keyframes pandaFloat {
           0%, 100% { transform: translateY(0px); }
           50%       { transform: translateY(-14px); }
         }

         @keyframes blobMorph {
           0%   { border-radius: 62% 38% 46% 54% / 60% 44% 56% 40%; transform: rotate(0deg)   scale(1);    }
           25%  { border-radius: 38% 62% 54% 46% / 44% 60% 40% 56%; transform: rotate(90deg)  scale(1.06); }
           50%  { border-radius: 54% 46% 38% 62% / 56% 40% 60% 44%; transform: rotate(180deg) scale(0.96); }
           75%  { border-radius: 46% 54% 62% 38% / 40% 56% 44% 60%; transform: rotate(270deg) scale(1.04); }
           100% { border-radius: 62% 38% 46% 54% / 60% 44% 56% 40%; transform: rotate(360deg) scale(1);    }
         }

         .hero-logo-wrapper {
           position: relative;
           width: 320px;
           height: 320px;
           display: flex;
           align-items: center;
           justify-content: center;
           margin: 0 auto 16px;
         }

         .hero-blob {
           position: absolute;
           width: 210px;
           height: 210px;
           background: radial-gradient(ellipse at 40% 40%,
             rgba(167, 139, 250, 0.28) 0%,
             rgba(232, 121, 249, 0.16) 50%,
             rgba(129, 140, 248, 0.12) 100%
           );
           animation: blobMorph 9s ease-in-out infinite;
           filter: blur(32px);
           pointer-events: none;
         }

.hero-logo {
           width: 155px;
           height: auto;
           position: relative;
           z-index: 2;
           animation: pandaFloat 3.8s ease-in-out infinite;
           filter:
             drop-shadow(0 10px 28px rgba(167, 139, 250, 0.35))
             drop-shadow(0 2px 8px rgba(0, 0, 0, 0.1));
         }

         .hero-subheading {
           font-size: 1.5rem;
           font-weight: 300;
           color: var(--text-primary);
           margin-top: 24px;
           opacity: 0.9;
           text-align: center;
         }

         .hero-actions {
           margin-top: 32px;
           display: flex;
           justify-content: center;
           gap: 20px;
           flex-wrap: wrap;
         }

         .play-store-btn {
           display: inline-block;
           transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
           filter: drop-shadow(0 0 0 rgba(16, 185, 129, 0));
         }

         .play-store-btn:hover {
           transform: scale(1.08);
           filter: drop-shadow(0 0 20px rgba(16, 185, 129, 0.4));
         }

         .play-store-img {
           height: 74px;
           width: auto;
         }

         .blog-btn {
          /* --- Sizing (to match Play Store button) --- */
          height: 60px;
          box-sizing: border-box; 
          padding: 0 32px; 

          /* --- Centering --- */
          display: inline-flex; 
          align-items: center;
          justify-content: center;

          /* --- Glass Styling --- */
          background: rgba(0, 0, 0, 0.05);
          border: 2px solid rgba(0, 0, 0, 0.12);
          border-radius: 12px;
          color: var(--text-primary);
          font-size: 1rem;
          font-weight: 600;
          text-decoration: none;
          backdrop-filter: blur(10px);
          
          /* --- Animation --- */
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .blog-btn:hover {
          background: rgba(0, 0, 0, 0.1);
          border-color: rgba(0, 0, 0, 0.22);
          transform: translateY(-2px);
          box-shadow: 0 10px 20px rgba(0, 0, 0, 0.3);
        }

        .section {
          background: var(--darker-color);
          position: relative;
          z-index: 2;
        }

        .section-header {
          text-align: center;
          margin-bottom: 64px;
        }

        .section-title {
          font-size: 2.5rem;
          font-weight: 700;
          color: var(--text-primary);
          margin-bottom: 16px;
        }

        .section-subtitle {
          font-size: 1.125rem;
          color: var(--text-secondary);
          max-width: 600px;
          margin: 0 auto;
        }

         .stats {
           background: var(--darker-color);
           padding: 40px 0 80px;
           margin-top: -80px;
           position: relative;
           z-index: 10;
         }

         .stats-grid {
           display: grid;
           grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
           gap: 40px;
           max-width: 800px;
           margin: 0 auto;
         }

         .stat-card {
           background: #FFFFFF;
           border: 1px solid rgba(0, 0, 0, 0.08);
           border-radius: 20px;
           padding: 40px 32px;
           text-align: center;
           box-shadow: 0 2px 8px rgba(0,0,0,0.06);
           transition: all 0.3s ease;
         }

         .stat-card:hover {
           border-color: rgba(5, 150, 105, 0.25);
           transform: translateY(-5px);
           box-shadow: 0 12px 32px rgba(0, 0, 0, 0.1);
         }

         .stat-icon {
           font-size: 3rem;
           margin-bottom: 16px;
           color: var(--text-primary);
         }

         .stat-number {
           font-size: 3rem;
           font-weight: 700;
           color: var(--text-primary);
           margin-bottom: 8px;
         }

         .stat-label {
           font-size: 1.25rem;
           font-weight: 600;
           color: var(--text-primary);
           margin-bottom: 8px;
         }

         .stat-description {
           font-size: 1rem;
           color: var(--text-primary);
           opacity: 0.8;
         }

         .github-link {
           display: flex;
           align-items: center;
           justify-content: center;
           gap: 8px;
           margin-top: 16px;
           padding: 8px 16px;
           background: rgba(5, 150, 105, 0.06);
           border: 1px solid rgba(5, 150, 105, 0.2);
           border-radius: 8px;
           text-decoration: none;
           color: var(--primary-color);
           font-size: 0.9rem;
           transition: all 0.3s ease;
         }

         .github-link:hover {
           background: rgba(5, 150, 105, 0.12);
           border-color: rgba(5, 150, 105, 0.35);
           transform: translateY(-2px);
         }

         .github-icon {
           width: 20px;
           height: 20px;
           filter: brightness(0) saturate(100%) invert(38%) sepia(98%) saturate(359%) hue-rotate(116deg) brightness(90%);
         }

         .repo-name {
           font-family: 'Courier New', monospace;
           font-weight: 500;
         }

         .video-showcase {
           background: var(--dark-color);
           padding: 120px 0;
           position: relative;
           overflow: hidden;
         }

         .video-showcase::before {
           content: '';
           position: absolute;
           top: 0; left: 0; right: 0;
           height: 250px;
           background: linear-gradient(to bottom, var(--darker-color) 0%, transparent 100%);
           z-index: 1;
           pointer-events: none;
         }

         .video-showcase::after {
           content: '';
           position: absolute;
           bottom: 0; left: 0; right: 0;
           height: 250px;
           background: linear-gradient(to top, var(--darker-color) 0%, transparent 100%);
           z-index: 1;
           pointer-events: none;
         }

         .background-video {
           position: absolute;
           top: 0;
           left: 0;
           width: 100%;
           height: 100%;
           object-fit: cover;
           z-index: 0;
           opacity: 0.06;
         }


         .video-showcase-container {
           display: flex;
           flex-direction: column;
           gap: 40px;
           position: relative;
           z-index: 2;
         }

         .video-side {
           display: grid;
           grid-template-columns: 1fr 1fr 1fr;
           gap: 30px;
         }

         .content-side {
           display: grid;
           grid-template-columns: 1fr 1fr 1fr;
           gap: 30px;
           padding: 20px 0;
         }

         .content-item {
           padding: 32px;
           background: #FFFFFF;
           border: 1px solid rgba(0, 0, 0, 0.08);
           border-radius: 16px;
           box-shadow: 0 2px 8px rgba(0,0,0,0.05);
           transition: all 0.3s ease;
         }

         .content-item:hover {
           border-color: rgba(5, 150, 105, 0.25);
           transform: translateY(-2px);
           box-shadow: 0 8px 20px rgba(0,0,0,0.09);
         }

         .content-item h3 {
           font-size: 1.5rem;
           font-weight: 600;
           color: var(--primary-color);
           margin-bottom: 16px;
         }

         .content-item p {
           font-size: 1rem;
           line-height: 1.6;
           color: var(--text-secondary);
           margin: 0;
         }

         .video-container {
           display: flex;
           flex-direction: column;
           gap: 40px;
           max-width: 1000px;
           margin: 0 auto;
         }

         .video-item {
           position: relative;
           border-radius: 20px;
           overflow: hidden;
           box-shadow: var(--shadow-xl);
           transition: transform 0.3s ease;
         }

         .video-item:hover {
           transform: scale(1.02);
         }

         .audio-toggle-btn {
           position: absolute;
           top: 20px;
           right: 20px;
           width: 40px;
           height: 40px;
           background: rgba(255,255,255,0.75);
           border: 1px solid rgba(0,0,0,0.15);
           border-radius: 50%;
           color: #111827;
           cursor: pointer;
           transition: all 0.3s ease;
           backdrop-filter: blur(8px);
           z-index: 10;
           display: flex;
           align-items: center;
           justify-content: center;
           font-size: 18px;
         }

         .audio-toggle-btn:hover {
           background: rgba(255,255,255,0.95);
           border-color: rgba(0,0,0,0.25);
           transform: scale(1.1);
         }

         .audio-icon {
           display: flex;
           align-items: center;
           justify-content: center;
           width: 100%;
           height: 100%;
         }

         .showcase-video {
           width: 100%;
           height: 500px;
           object-fit: cover;
           display: block;
         }

         .benefits {
           background: var(--darker-color);
         }

        .benefits-content {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 64px;
          align-items: center;
        }

        .benefits-list {
          margin-top: 32px;
        }

        .benefit-item {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 16px;
          font-size: 1.125rem;
          color: var(--text-primary);
        }

        .benefit-icon {
          color: var(--success-color);
          flex-shrink: 0;
        }

        .benefits-card {
          background: rgba(30, 30, 46, 0.8);
          border: 1px solid rgba(16, 185, 129, 0.2);
          border-radius: 20px;
          padding: 32px;
          box-shadow: var(--shadow-lg);
          backdrop-filter: blur(10px);
        }

        .card-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 16px;
        }

        .card-header h3 {
          font-size: 1.25rem;
          font-weight: 600;
          color: var(--text-primary);
        }

        .card-stats {
          margin-top: 24px;
        }

        .mini-stat {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 16px;
          background: var(--light-color);
          border-radius: 12px;
        }

        .mini-stat-number {
          font-size: 1.125rem;
          font-weight: 600;
          color: var(--primary-color);
        }

        .mini-stat-label {
          font-size: 0.875rem;
          color: var(--text-secondary);
        }

        .waitlist {
          background: var(--gradient-primary);
          color: var(--text-primary);
          text-align: center;
        }

        .waitlist-content {
          max-width: 600px;
          margin: 0 auto;
        }

        .waitlist .section-title,
        .waitlist .section-subtitle {
          color: var(--text-primary);
        }

        .waitlist-note {
          margin-top: 24px;
          font-size: 0.875rem;
          opacity: 0.8;
        }

        .contact-email-text {
          font-size: 1rem;
          opacity: 0.8;
          margin: 0;
        }

        .write-to-us-link {
          display: inline-block;
          font-size: 0.9rem;
          color: var(--text-secondary); /* This is your light grey color */
          text-decoration: underline;
          margin-top: 8px; /* Adds a little space */
          transition: color 0.3s ease;
        }

        .write-to-us-link:hover {
          color: var(--text-primary); /* Changes to white on hover */
        }



        /* Pricing Section */
        .pricing {
          background: var(--darker-color);
          padding: 80px 0;
        }

        .pricing-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 30px;
          max-width: 1000px;
          margin: 0 auto;
          align-items: start;
        }

        .pricing-card {
          background: #FFFFFF;
          border: 1px solid rgba(0, 0, 0, 0.08);
          border-radius: 20px;
          padding: 36px 28px;
          text-align: center;
          box-shadow: 0 2px 8px rgba(0,0,0,0.06);
          transition: all 0.3s ease;
          position: relative;
        }

        .pricing-card:hover {
          border-color: rgba(5, 150, 105, 0.25);
          transform: translateY(-5px);
          box-shadow: 0 16px 40px rgba(0, 0, 0, 0.1);
        }

        .pricing-card-featured {
          border-color: rgba(16, 185, 129, 0.5);
          background: rgba(16, 185, 129, 0.08);
          transform: scale(1.03);
        }

        .pricing-card-featured:hover {
          border-color: rgba(16, 185, 129, 0.7);
          transform: scale(1.03) translateY(-5px);
        }

        .pricing-badge {
          display: inline-block;
          padding: 4px 14px;
          background: rgba(0, 0, 0, 0.05);
          border: 1px solid rgba(0, 0, 0, 0.1);
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 600;
          color: var(--text-secondary);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 16px;
        }

        .pricing-badge-pro {
          background: rgba(16, 185, 129, 0.2);
          border-color: rgba(16, 185, 129, 0.4);
          color: var(--primary-color);
        }

        .pricing-plan {
          font-size: 1.75rem;
          font-weight: 700;
          color: var(--text-primary);
          margin-bottom: 12px;
        }

        .pricing-price {
          margin-bottom: 8px;
          display: flex;
          align-items: baseline;
          justify-content: center;
          gap: 4px;
        }

        .price-amount {
          font-size: 2.5rem;
          font-weight: 700;
          color: var(--text-primary);
        }

        .price-period {
          font-size: 1rem;
          color: var(--text-secondary);
        }

        .pricing-description {
          font-size: 0.9rem;
          color: var(--text-secondary);
          margin-bottom: 24px;
        }

        .pricing-yearly {
          font-size: 0.85rem;
          color: var(--success-color);
          margin-bottom: 8px;
          font-weight: 500;
        }

        .pricing-features {
          list-style: none;
          padding: 0;
          margin: 0 0 28px 0;
          text-align: left;
        }

        .pricing-features li {
          padding: 8px 0;
          font-size: 0.95rem;
          color: var(--text-secondary);
          border-bottom: 1px solid rgba(0, 0, 0, 0.04);
        }

        .pricing-features li:last-child {
          border-bottom: none;
        }

        .pricing-cta {
          display: inline-block;
          width: 100%;
          padding: 14px 24px;
          background: rgba(0, 0, 0, 0.05);
          border: 1px solid rgba(0, 0, 0, 0.12);
          border-radius: 12px;
          color: var(--text-primary);
          text-decoration: none;
          font-weight: 600;
          font-size: 0.95rem;
          transition: all 0.3s ease;
          box-sizing: border-box;
        }

        .pricing-cta:hover {
          background: rgba(0, 0, 0, 0.1);
          border-color: rgba(0, 0, 0, 0.22);
          transform: translateY(-2px);
        }

        .pricing-cta-pro {
          background: var(--gradient-primary);
          border: 1px solid rgba(16, 185, 129, 0.5);
          box-shadow: var(--glow-primary);
        }

        .pricing-cta-pro:hover {
          box-shadow: 0 0 30px rgba(16, 185, 129, 0.5);
        }

        /* Social Links */
        .social-links {
          display: flex;
          justify-content: center;
          gap: 16px;
          margin-bottom: 32px;
          flex-wrap: wrap;
        }

        .social-link {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 44px;
          height: 44px;
          background: rgba(0, 0, 0, 0.05);
          border: 1px solid rgba(0, 0, 0, 0.1);
          border-radius: 50%;
          color: rgba(17, 24, 39, 0.65);
          text-decoration: none;
          transition: all 0.3s ease;
        }

        .social-link:hover {
          background: rgba(0, 0, 0, 0.1);
          border-color: rgba(0, 0, 0, 0.18);
          color: var(--text-primary);
          transform: translateY(-3px);
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
        }

        @media (max-width: 768px) {
          .hero {
            padding: 60px 0;
            min-height: auto;
          }

           .hero-heading {
             font-size: 1.9rem;
           }

           .hero-company-tag {
             font-size: 0.7rem;
           }

           .hero-description {
             font-size: 0.95rem;
           }

           .play-store-img {
             height: 50px;
           }

           .features-grid {
             grid-template-columns: 1fr 1fr;
             gap: 16px;
           }

           .feature-card {
             padding: 24px 16px;
           }

           .feature-icon {
             font-size: 2rem;
           }

           .feature-title {
             font-size: 1rem;
           }

           .impact-content {
             grid-template-columns: 1fr;
             gap: 32px;
           }

           .impact-quote {
             font-size: 1rem;
           }

           .stats-grid {
             grid-template-columns: 1fr;
             gap: 30px;
           }

           .stat-card {
             padding: 32px 24px;
           }

           .stat-number {
             font-size: 2.5rem;
           }

           .stat-label {
             font-size: 1.125rem;
           }

           .stat-description {
             font-size: 0.9rem;
           }

           .github-link {
             padding: 6px 12px;
             font-size: 0.8rem;
           }

           .github-icon {
             width: 16px;
             height: 16px;
           }

           .video-showcase-container {
             flex-direction: column;
             gap: 30px;
           }

           .video-side {
             grid-template-columns: 1fr;
             gap: 20px;
           }

           .content-side {
             grid-template-columns: 1fr;
             gap: 20px;
             padding: 0;
           }

           .content-item {
             padding: 24px;
           }

           .content-item h3 {
             font-size: 1.25rem;
           }

           .content-item p {
             font-size: 0.9rem;
           }

           .showcase-video {
             height: 350px;
           }

           .audio-toggle-btn {
             top: 16px;
             right: 16px;
             width: 36px;
             height: 36px;
             font-size: 16px;
           }

          .section-title {
            font-size: 2rem;
          }

          .benefits-content {
            grid-template-columns: 1fr;
            gap: 40px;
          }

          .footer-content {
            flex-direction: column;
            gap: 24px;
            text-align: center;
          }

          .footer-links {
            justify-content: center;
          }



          .pricing-grid {
            grid-template-columns: 1fr;
            gap: 24px;
            max-width: 400px;
          }

          .pricing-card-featured {
            transform: none;
          }

          .pricing-card-featured:hover {
            transform: translateY(-5px);
          }

          .price-amount {
            font-size: 2rem;
          }
        }
      `}</style>
    </div>
  );
}


// Wrap app content to use location
function AppContent() {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <HomePage />
          </motion.div>
        } />
        <Route path="/blog" element={
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <BlogList />
          </motion.div>
        } />
        <Route path="/blog/:id" element={
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <BlogPost />
          </motion.div>
        } />
        <Route path="/privacy-policy" element={
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <PrivacyPolicy />
          </motion.div>
        } />
        <Route path="/team" element={
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Team />
          </motion.div>
        } />
      </Routes>
    </AnimatePresence>
  );
}

function App() {
  return (
    <Router>
      <NeuralBg />
      <ScrollToTop />
      <AppContent />
      <WalkingPanda />
    </Router>
  );
}

export default App; 